import React, {Fragment, useCallback, useContext, useEffect, useState, useMemo} from 'react';
import NumberFormat from 'react-number-format';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import * as MaterialityAction from '../../../../../actions/Materiality/materialityactions';
import {getCurrentMateriality, getPriorYearMateriality} from '../../../../../selectors/Ares/materiality';
import WrapComponentAroundBraces from '../../../../../util/React/WrapComponentAroundBraces';
import {formBodyType, positiveNullableIntegerRegex} from '../../../../../util/uiconstants';
import {isNullOrUndefined, labels, setThousandSeparator, GetCardTypeForBody, isNullOrEmpty} from '../../../../../util/utils';
import {DocumentTypeIdContext} from '../../../../Main/Forms/CanvasFormContainer';
import SelectDropdown from '../../../../Common/SelectDropdown/SelectDropdown';
import CanvasFormInput from '../../CanvasFormInput';
import BodyKnowledgeDescription from '../BodyKnowledgeDescription';
import TEMaterialityDisplayCurrentPercentage from './TEMaterialityDisplayCurrentPercentage';
import TEMaterialityDisplayNumber from './TEMaterialityDisplayNumber';
import CanvasFormTextAreaValue from '../../CanvasFormTextAreaValue';
import CanvasFormTextArea from '../../CanvasFormTextArea';
import {TEMaterialityDisplay, calculateBufferedValue} from './materiality.common';
import {getUnresolvedCommentsCount} from '../../../../../actions/ValidationControl/validationcontrolactions';
import Utility from '../../../../../util/utilityfunctions';
import {IconoirWarningTriangle} from '@ey-xd/motif-icon';
import {FormBodyChangesContext} from '../FormBodyChangesWatcher';
import ProgressBar from '../../../BaseComponentWrappers/MotifProgressBar';
import Icon from '../../../BaseComponentWrappers/MotifIcon';
import {useDocumentId, useEngagementId, useUserPreferences} from '../../../../../util/customHooks';
import _ from 'lodash';

function TEMateriality({
	bodyId,
	bodyRelatedData,
	getBodiesBySectionId,
	guidanceFilter,
	headerId,
	isAresView,
	isSnapshotView,
	isDisabled,
	sectionId,
	showGuidance,
	getMateriality
}) {
	const engagementId = useEngagementId(),
		documentId = useDocumentId(),
		userPreferences = useUserPreferences();

	const documentTypeId = useContext(DocumentTypeIdContext),
		{resetChanges, onBodyValueChanged} = useContext(FormBodyChangesContext);

	const priorYearMateriality = useSelector(getPriorYearMateriality),
		currentMateriality = useSelector(getCurrentMateriality),
		formBodyDisplayDataForUpdating = useSelector((store) => store.formBodyDisplayData);

	const currentTERationale = !isNullOrUndefined(currentMateriality?.teRationale)
		? currentMateriality.teRationale
		: undefined;

	const [showOverlay, setShowOverlay] = useState(false),
		[disableTePercentage, setDisableTePercentage] = useState(false),
		[fakePercentageOption, setFakePercentageOption] = useState(),
		[teAmount, setTeAmount] = useState(currentMateriality.teAmount),
		[teRationale, setTERationale] = useState(currentTERationale),
		[showTERationale, setShowTERationale] = useState(false),
		[selectedTePercentageValue, setSelectedTePercentageValue] = useState(options),
		[isTeValid, setIsTeValid] = useState(true);

	const teAmountInput = 'TEinput',
		teRationaleTextArea = 'rationale-container';

	const featureConfigurations = useSelector((store) => store.configurations);

	const initialTeAmmount =
		teAmount === 'TEinput' ? currentMateriality.pmAmount : isNullOrUndefined(teAmount) ? '' : teAmount;

	const maxPercentageDecimals = 0;

	const isHighRiskPresent = useMemo(() => {
		return currentMateriality && currentMateriality.suggestedTEPercent < 0;
	}, [currentMateriality]);

	// isValid property is to distinguish between a valid dropdown option and a custom percentage (ie calculated from TE amount)
	const percentagesOptions = [
		{value: 0, label: '50', isValid: true},
		{value: 1, label: '75', isValid: true}
	];

	const tePercentageList = useMemo(() => {
		return isHighRiskPresent ? percentagesOptions.filter((x) => x.value === 0) : percentagesOptions;
	}, [isHighRiskPresent]);

	const valFormatter = setThousandSeparator(userPreferences.numberFormat);

	const options = tePercentageList.find((fl) => fl.value === 0);

	const formBodyBorder = GetCardTypeForBody(bodyRelatedData, isAresView);

	const suggestedTEPercentDisplayValue = isHighRiskPresent
		? Utility.applyRounding((Math.abs(currentMateriality.pmAmount) * 50) / 100, 0)
		: Utility.applyRounding((Math.abs(currentMateriality.suggestedTEPercent) / 100) * currentMateriality.pmAmount, 0);

	const dispatch = useDispatch();

	const teRationalInputHandleOnAccept = (value) => {
		setTERationale(value);
		setShowOverlay(true);
		return dispatch(
			MaterialityAction.patchMateriality(engagementId, currentMateriality.id, {
				materialityAmount: currentMateriality.teAmount,
				bodyTypeId: formBodyType.TolerableError,
				teRationale: value.toString().trim()
			})
		).then(() => {
			onInputCancel(teRationaleTextArea);
			setShowOverlay(false);
		});
	};

	const getShowTERationale = useCallback(() => {
		let canshow = TEMaterialityDisplay(
			currentMateriality,
			featureConfigurations?.features?.materialityTERationaleRoundingBufferPercentage,
			currentTERationale
		);
		// setShowTERationale(canshow);
		setShowTERationale((prevValue) => {
			//If we do not show the rationale due to the percentage being outside of the range, the text area goes back to the initial state. Thus we need to reset
			//the store's hasUnsavedChanges to false.
			//Secondly, we only set the flag to false for the rationale when we were showing it previously to begin with.
			if (prevValue && !canshow) {
				onInputCancel(teRationaleTextArea);
			}
			return canshow;
		});
	}, [currentMateriality, featureConfigurations, currentTERationale, onInputCancel]);

	const getTEPercentageFromAmount = useCallback(
		(teAmountValue) => {
			let percentageDetails;

			if (teAmountValue) {
				const teBufferPercentage = featureConfigurations?.features?.materialityTERationaleRoundingBufferPercentage;
				let teRoundingBufferPercentage;

				if (isHighRiskPresent || !teBufferPercentage) {
					teRoundingBufferPercentage = 0.05;
				} else {
					teRoundingBufferPercentage = teBufferPercentage / 100;
				}

				// 75% of PM AMount buffers
				const teBufferHighRange75 =
					currentMateriality.pmAmount &&
					calculateBufferedValue(0.75, currentMateriality.pmAmount, 'high', teRoundingBufferPercentage);
				const teBbufferLowRange75 =
					currentMateriality.pmAmount &&
					calculateBufferedValue(0.75, currentMateriality.pmAmount, 'low', teRoundingBufferPercentage);

				// 50% of PM AMount buffers
				const teBufferHighRange50 =
					currentMateriality.pmAmount &&
					calculateBufferedValue(0.5, currentMateriality.pmAmount, 'high', teRoundingBufferPercentage);
				const teBufferLowRange50 =
					currentMateriality.pmAmount &&
					calculateBufferedValue(0.5, currentMateriality.pmAmount, 'low', teRoundingBufferPercentage);

				const isTEAmountInPercentage75 = teBufferHighRange75 >= teAmountValue && teAmountValue >= teBbufferLowRange75;
				const isTEAmountInPercentage50 = teBufferHighRange50 >= teAmountValue && teAmountValue >= teBufferLowRange50;

				if (!isHighRiskPresent && isTEAmountInPercentage75) {
					percentageDetails = tePercentageList.find((fl) => fl.value === 1);
				} else if (isTEAmountInPercentage50) {
					percentageDetails = tePercentageList.find((fl) => fl.value === 0);
				} else {
					const roundedPercentage =
						currentMateriality.pmAmount &&
						Utility.applyRounding(
							(teAmountValue * 100) / currentMateriality.pmAmount,
							maxPercentageDecimals
						).toString();

					percentageDetails = !isNullOrEmpty(roundedPercentage)
						? {
								value: -1,
								label: roundedPercentage,
								isValid: false
						  }
						: undefined;
				}
			}

			return percentageDetails;
		},
		[currentMateriality.pmAmount, tePercentageList, isHighRiskPresent]
	);

	const updateTEPercentageState = useCallback(
		(teAmountValue) => {
			const percentageDetails = getTEPercentageFromAmount(teAmountValue);
			if (percentageDetails) {
				setFakePercentageOption(percentageDetails.isValid ? undefined : percentageDetails.label);
				setSelectedTePercentageValue(percentageDetails.isValid ? percentageDetails : {});
				setIsTeValid(percentageDetails.isValid);

				const _currentMateriality = {
					...currentMateriality,
					teAmount: Number(teAmountValue)
				};

				const _showRationale = TEMaterialityDisplay(
					_currentMateriality,
					featureConfigurations?.features?.materialityTERationaleRoundingBufferPercentage,
					currentTERationale
				);
				setShowTERationale(_showRationale);
			} else {
				setFakePercentageOption(undefined);
				setSelectedTePercentageValue(undefined);
				setIsTeValid(isNullOrUndefined(teAmountValue));
			}
		},
		[getTEPercentageFromAmount, currentMateriality, currentTERationale]
	);

	const TEPercentDropdownHandleOnChange = useCallback(
		(option) => {
			const selectedValue = tePercentageList.find((fl) => fl.value === option.value);
			if (currentMateriality && currentMateriality.pmAmount) {
				let tePercentage = parseFloat(option.label);
				let valuetoupdate = Utility.applyRounding((tePercentage * currentMateriality.pmAmount) / 100, 0);
				setShowOverlay(true);
				dispatch(
					MaterialityAction.patchMateriality(engagementId, currentMateriality.id, {
						materialityAmount: Number(valuetoupdate),
						suggestedTEPercent: Number(tePercentage),
						bodyTypeId: formBodyType.TolerableError,
						teRationale: teRationale
					})
				).then(() => {
					onInputCancel(teAmountInput);
					setTeAmount(valuetoupdate);
					setShowOverlay(false);
					dispatch(getUnresolvedCommentsCount(engagementId, documentId, documentTypeId));
				});
			}
			setSelectedTePercentageValue(selectedValue);
		},
		[currentMateriality, dispatch, engagementId, tePercentageList]
	);

	const TEAmountInputHandleOnAccept = useCallback(
		async (value) => {
			let newValue = value ? Utility.applyRounding(Number(value), 0) : null;
			newValue = newValue ? newValue.toString().trim() : null;

			setShowOverlay(true);
			return dispatch(
				MaterialityAction.patchMateriality(engagementId, currentMateriality.id, {
					materialityAmount: Number(!isNullOrUndefined(newValue) ? newValue : 'NULL'),
					bodyTypeId: formBodyType.TolerableError,
					teRationale: teRationale
				})
			).then(() => {
				setTeAmount(newValue);
				updateTEPercentageState(newValue);
				setShowOverlay(false);
				onInputCancel(teAmountInput);
				//Clear state TE rationale value
				if (!showTERationale) {
					setTERationale();
				}
			});
		},
		[currentMateriality.id, dispatch, engagementId, teAmount, updateTEPercentageState, isTeValid]
	);

	const onInputChange = useCallback(
		(initialValue, newVale, elementName) => {
			onBodyValueChanged(bodyId, initialValue, newVale, elementName);
		},
		[bodyId, onBodyValueChanged]
	);

	const onInputCancel = useCallback(
		(elementName) => {
			resetChanges(bodyId, elementName);
		},
		[bodyId, resetChanges]
	);

	useEffect(() => {
		updateTEPercentageState(currentMateriality.teAmount);
		getShowTERationale();
	}, [currentMateriality, updateTEPercentageState, dispatch, engagementId]);

	useEffect(() => {
		if (formBodyDisplayDataForUpdating && formBodyDisplayDataForUpdating.isMaterilityUpdate) {
			getMateriality();
		}
	}, [formBodyDisplayDataForUpdating, dispatch, engagementId]);

	return (
		<StyledTEMateriality
			className={`SectionBodyWrapper aresSectionBodyWrapper SectionBodyTEMWrapper ${formBodyBorder}`}
		>
			<Fragment>
				<BodyKnowledgeDescription
					bodyDescriptionText={bodyRelatedData.name}
					bodyId={bodyId}
					bodyModel={bodyRelatedData}
					documentId={documentId}
					engagementId={engagementId}
					getBodiesBySectionId={getBodiesBySectionId}
					guidanceFilter={guidanceFilter}
					headerId={headerId}
					isAresView={isAresView}
					isCustom={bodyRelatedData.isCustom}
					isDuplicate={bodyRelatedData.isDuplicate}
					isSnapshotView={isSnapshotView}
					sectionId={sectionId}
					showGuidance={showGuidance}
				/>
				<StyledSectionBodyTEMData className={`SectionBodyData aresSectionBodyData SectionBodyTEMData`} id={bodyId}>
					<Fragment>
						{showOverlay && (
							<section className="motif-progress-wrapper">
								<ProgressBar isIndeterminate hideLabel circle />
							</section>
						)}
						<StyledLabelNameValueWrapper className="LabelNameValueWrapper aresLabelNameValueWrapper PMWrapper">
							<StyledLabelNameWrapper className="LabelName aresLabelName">{`${labels.planningmateriality}:`}</StyledLabelNameWrapper>
							<StyledLabelValueWrapper className="LabelValue aresLabelValue">
								{currentMateriality && currentMateriality.pmAmount ? (
									<TEMaterialityDisplayNumber value={currentMateriality.pmAmount} />
								) : (
									'-'
								)}
							</StyledLabelValueWrapper>
						</StyledLabelNameValueWrapper>
						{priorYearMateriality && priorYearMateriality.teAmount && (
							<StyledLabelNameValueWrapper className="LabelNameValueWrapper aresLabelNameValueWrapper PPTEWrapper">
								<StyledLabelNameWrapper className="LabelName aresLabelName">{`${labels.priorperiodte}:`}</StyledLabelNameWrapper>
								<StyledLabelValueWrapper className="LabelValue aresLabelValue">
									{priorYearMateriality && priorYearMateriality.teAmount && priorYearMateriality.pmAmount && (
										<TEMaterialityDisplayCurrentPercentage
											teAmountValue={priorYearMateriality.teAmount}
											pmAmountValue={priorYearMateriality.pmAmount}
											isCurrentTe={false}
										/>
									)}
									<WrapComponentAroundBraces>
										<TEMaterialityDisplayNumber
											className="DisplayAmountCount no-margin"
											value={priorYearMateriality.teAmount}
										/>
									</WrapComponentAroundBraces>
								</StyledLabelValueWrapper>
							</StyledLabelNameValueWrapper>
						)}
						{currentMateriality && currentMateriality.suggestedTEPercent && (
							<StyledLabelNameValueWrapper className="LabelNameValueWrapper aresLabelNameValueWrapper STEWrapper">
								<StyledLabelNameWrapper className="LabelName aresLabelName">
									{isHighRiskPresent ? labels.requiredTePercentage : labels.suggestedtepercentage}:
								</StyledLabelNameWrapper>
								<StyledLabelValueWrapper className="LabelValue aresLabelValue">
									<span className="apply-color">
										{
											<TEMaterialityDisplayNumber
												value={Math.abs(currentMateriality.suggestedTEPercent)}
												suffix={'%'}
											/>
										}
									</span>
									{currentMateriality && currentMateriality.pmAmount ? (
										<WrapComponentAroundBraces>
											<TEMaterialityDisplayNumber
												className="DisplayAmountCount no-margin"
												value={suggestedTEPercentDisplayValue}
											/>
										</WrapComponentAroundBraces>
									) : (
										<StyledLabelValueWrapper className="LabelValue">{'(--)'}</StyledLabelValueWrapper>
									)}
								</StyledLabelValueWrapper>
								<MaterialityInformationLine className="MaterialityInformationLine">
									<span className="materiality-information-line__label aresLabelName">
										{isHighRiskPresent ? labels.highRiskTERequired : labels.suggestedTEPercentageInfo}
									</span>
								</MaterialityInformationLine>
							</StyledLabelNameValueWrapper>
						)}
						<StyledTEWrapper className={showTERationale ? 'TEWrapper applyMargin' : 'TEWrapper'}>
							<section className={`TEPercentDropdownWrapper`}>
								<SelectDropdown
									className={'TEPercentDropdown'}
									formLabel={labels.tepercentage}
									isSearchable
									id="TEPercent"
									formatOptionLabel={({label}) => {
										return _.isEmpty(label) ? '' : `${label}%`;
									}}
									options={tePercentageList}
									noOptionsMessage={() => labels.noResultsFound}
									isClearable={false}
									isDisabled={
										isDisabled ||
										isNullOrUndefined(currentMateriality.pmAmount) ||
										currentMateriality.pmAmount === 0 ||
										disableTePercentage
											? true
											: false
									}
									defaultValue={selectedTePercentageValue}
									value={
										selectedTePercentageValue
											? _.isEmpty(selectedTePercentageValue)
												? {value: -1, label: `${fakePercentageOption || ''}`}
												: selectedTePercentageValue
											: ''
									}
									onChange={TEPercentDropdownHandleOnChange}
								/>
							</section>
							<section
								className={`${
									teAmount === 'TEinput'
										? currentMateriality.pmAmount
										: isNullOrUndefined(teAmount)
										? ''
										: teAmount
										? 'TEAmountInputValue'
										: ''
								}`}
							>
								<CanvasFormInput
									className={`canvas-form-input inputFieldWrapper TEAmountInput`}
									type="integer"
									id={teAmountInput}
									name={teAmountInput}
									label={labels.teamount}
									maxlength={15}
									onChange={(newValue) => onInputChange(initialTeAmmount, newValue, teAmountInput)}
									onCancel={() => onInputCancel(teAmountInput)}
									onAccept={TEAmountInputHandleOnAccept}
									pattern={positiveNullableIntegerRegex}
									showLabel
									value={initialTeAmmount}
									showProgressBar={false}
									disabled={isDisabled}
								/>
							</section>
						</StyledTEWrapper>
						{!isTeValid && (
							<StyledInvalidTeAmmount
								className="StyledInvalidTeAmmount validationErrorMessage "
								src={IconoirWarningTriangle}
							>
								<Icon src={IconoirWarningTriangle} />
								<span className="te-ammount-invalid-text errorText">
									{isHighRiskPresent ? labels.highRiskTEAmmountInvalid : labels.teAmmountInvalid}
								</span>
							</StyledInvalidTeAmmount>
						)}
						{showTERationale && !isHighRiskPresent && (
							<StyledTERationaleWrapper className="TERationaleWrapper">
								<section className="rationale-input">
									<span>{labels.rationaleTEDescription}</span>
									<CanvasFormTextArea
										id={teRationaleTextArea}
										name={teRationaleTextArea}
										label={labels.enterRationale}
										onChange={(e) => onInputChange(currentTERationale, e.target.value, teRationaleTextArea)}
										onCancel={() => onInputCancel(teRationaleTextArea)}
										onAccept={teRationalInputHandleOnAccept}
										showLabel
										maxLength={500}
										progressBarClassName="saverationale-loader"
										value={teRationale}
										isAresView={isAresView}
										showProgressBar={false}
										disabled={isDisabled}
									>
										<CanvasFormTextAreaValue isSnapshotView={isSnapshotView} />
									</CanvasFormTextArea>
								</section>
							</StyledTERationaleWrapper>
						)}
					</Fragment>
				</StyledSectionBodyTEMData>
			</Fragment>
		</StyledTEMateriality>
	);
}

const StyledInvalidTeAmmount = styled.section`
	align-items: flex-start;

	.motif-icon {
		svg {
			width: var(--px-20) !important;
			height: var(--px-20) !important;
			margin-top: var(--px-2);
			fill: var(--red-600);
		}
	}
	.te-ammount-invalid-text {
		font-size: var(--px-14) !important;
		margin-left: var(--px-10);
	}
	&.validationErrorMessage {
		color: var(--red-600);
		width: var(--px-400);
		margin-top: var(--px-10) !important;
		display: flex;
		align-items: center;
		.motif-icon {
			display: inline-flex;
			align-items: center;
			margin-right: var(--px-3);
			svg {
				width: var(--px-20) !important;
				height: var(--px-20) !important;
				fill: var(--red-600) !important;
				cursor: default;
				path {
					fill: var(--red-600) !important;
				}
			}
		}
		.errorText {
			color: var(--error);
			font-size: var(--px-12) !important;
			font-weight: 300;
			line-height: var(--px-20);
			margin: 0;
		}
	}
`;

const StyledTERationaleWrapper = styled.section`
	position: relative;
	display: flex;
	flex-direction: column;
	.rationale-input {
		margin: var(--px-40) 0 var(--px-10) 0;
		.rationaleDescriptionText {
			font-size: var(--px-14);
			color: var(--neutrals-900);
		}
		.canvas-form-textarea {
			width: 50%;
			margin-top: var(--px-15);
		}
	}

	.canvas-form-input {
		width: var(--px-320) !important;
		.motif-form-field {
			width: var(--px-320) !important;
		}
	}
	.rationale-container {
		display: inline;
		position: relative;
		width: inherit;
		margin-top: var(--px-10);
		margin-bottom: var(--px-10);
	}
	&.TERationaleWrapper {
		.rationale-input {
			margin: var(--px-16) 0 0 0 !important;
			> span {
				font-weight: 300;
				font-size: var(--px-14);
				line-height: var(--px-24);
			}
			.canvas-form-textarea {
				width: var(--px-656) !important;
				margin-top: var(--px-8);
				.cancel-btn {
					margin: 0 0 0 var(--px-10);
				}
			}
		}
	}
`;

const StyledTEWrapper = styled.section`
	display: flex;
	align-items: center;
	.TEPercentDropdownWrapper {
		display: flex;
		width: var(--px-320);
		margin-right: var(--px-16);
		.TEPercentDropdown {
			width: var(--px-320) !important;
		}

		&.greyOutArea {
			position: relative;
			pointer-events: none;
			color: var(--neutrals-900);
			.MotifProgressBarWrapper {
				display: flex;
			}
			.TEPercentDropdown {
				background-color: var(--neutrals-200);
				opacity: 0.3;
			}
		}
		.MotifProgressBarWrapper {
			display: none;
			position: absolute;
			justify-content: center;
			width: 80%;
			height: 100%;
			.motif-progress-bar-wrapper {
				height: 100%;
				z-index: 1;
				justify-content: center;
				span {
					color: var(--green-500);
					font-size: var(--px-12);
				}
				.motif-progress {
					background-color: var(--green-500);
				}
			}
		}
	}
	.TEAmountInputValue {
		.canvas-form-input {
			.motif-form-field {
				.StyledCanvasFormInputDisplay {
					.motif-label {
						top: 0;
						position: absolute;
					}
				}
			}
		}
	}
	.canvas-form-input {
		width: var(--px-320) !important;
		.StyledCanvasFormInputDisplay {
			border: 0;
			height: inherit;
		}
		.motif-form-field {
			width: var(--px-320) !important;
		}
	}
`;

const MaterialityInformationLine = styled.span`
	font-size: var(--px-14);
	padding-bottom: var(--px-15);
	margin-left: var(--px-5);
	display: flex;
	max-width: calc(100% - var(--px-380));
	.materiality-information-line__label {
		display: inline-block;
		font-weight: 300;
	}
`;

const StyledLabelNameWrapper = styled.span`
	font-weight: 300;
	margin-right: var(--px-5);
`;

const StyledLabelValueWrapper = styled.span`
	font-weight: 400;
	.DisplayAmountCount {
		display: inline-flex;
		margin: 0 var(--px-5);
		&.no-margin {
			margin: unset;
		}
	}
`;

const StyledLabelNameValueWrapper = styled.section`
	display: flex;
	font-size: var(--px-14);
	margin-bottom: var(--px-15);
	line-height: normal;
	&.aresLabelNameValueWrapper {
		margin-bottom: var(--px-8);
		.aresLabelName {
			font-size: var(--px-14) !important;
			font-weight: 300 !important;
			line-height: var(--px-20) !important;
			color: var(--neutrals-900);
		}
		.aresLabelValue {
			font-size: var(--px-14) !important;
			font-weight: 400 !important;
			line-height: var(--px-20) !important;
			color: var(--neutrals-900);
		}
	}
`;

const StyledSectionBodyTEMData = styled.section`
	margin-top: var(--px-15);
	position: relative;
	.motif-progress-wrapper {
		position: absolute;
		width: calc(100% - 4rem);
		height: calc(100% - 4rem);
	}
	.LabelNameValueWrapper {
		&.aresLabelNameValueWrapper {
			&.STEWrapper {
				align-items: flex-start !important;
				.aresLabelName {
					white-space: nowrap;
				}
				.MaterialityInformationLine {
					margin-left: var(--px-10);
					.aresLabelName {
						white-space: unset !important;
					}
				}
			}
		}
	}
	&.aresSectionBodyData {
		position: relative;
		margin-top: var(--px-24);
		padding: var(--px-24);
		border-radius: var(--px-12) !important;
		border: var(--px-1) solid var(--neutrals-200);
		background-color: var(--neutrals-00white);
	}
`;

const StyledTEMateriality = styled.section`
	display: block;

	.motif-tooltip-wrapper {
		width: calc(100% - var(--px-30));
	}
	.inputFieldWrapper {
		display: flex;
		width: var(--px-400);
		position: relative;
		&.greyOutArea {
			position: relative;
			pointer-events: none;
			color: var(--neutrals-900);
			.MotifProgressBarWrapper {
				display: flex;
			}
			&.TEAmountInput {
				.inputField {
					background-color: var(--neutrals-200);
					opacity: 0.3;
				}
			}
		}
		.MotifProgressBarWrapper {
			display: none;
			position: absolute;
			justify-content: center;
			width: 80%;
			height: 100%;
			.motif-progress-bar-wrapper {
				height: 100%;
				z-index: 1;
				justify-content: center;
				span {
					color: var(--green-500);
					font-size: var(--px-12);
				}
				.motif-progress {
					background-color: var(--green-500);
				}
			}
		}
	}
	.inputFieldEditWrapper {
		display: flex;
		width: var(--px-400);
		.editInputIconWrapper {
			display: flex;
			align-items: center;
			height: var(--px-45);
			padding: var(--px-12) 0 var(--px-5) 0;
			margin-left: var(--px-5);
			.inputMotifIcon {
				color: var(--blue-600);
				font-size: var(--px-14);
				margin-left: var(--px-10);
				svg {
					fill: var(--neutrals-700);
				}
				&.saveBtn {
					svg {
						fill: var(--green-500);
					}
				}
				&.cancelBtn {
					svg {
						fill: var(--red-600);
					}
				}
			}
		}
	}

	.TEAmountInput {
		width: var(--px-280) !important;
		&.inputFieldWrapper {
			width: var(--px-320) !important;
		}
		&.inputFieldNoEdit {
			width: var(--px-280) !important;
		}
		.inputField {
			width: var(--px-280) !important;
			height: var(--px-400);
		}
		.inputFieldEditWrapper {
			width: unset;
			.inputField {
				background-color: var(--neutrals-200);
			}
		}
	}
	&.SectionBodyTEMWrapper {
		.StyledInvalidTeAmmount {
			margin: var(--px-6) 0 0 var(--px-340) !important;
		}
		.SectionBodyTEMData {
			.motif-progress-bar-wrapper.motif-progress-bar-wrapper-circle {
				width: var(--px-20) !important;
				height: var(--px-20) !important;
				top: 50%;
				left: 50%;
			}
			.StyledInvalidTeAmmount {
				svg path {
					fill: var(--red-600) !important;
				}
				.te-ammount-invalid-text {
					color: var(--error) !important;
				}
			}
			.STEWrapper {
				margin: 0 0 var(--px-24) 0 !important;
				.MaterialityInformationLine {
					padding: 0;
					font-size: var(--px-12);
					line-height: var(--px-20);
					max-width: 100%;
					margin: 0 0 0 var(--px-5) !important;
					align-items: center;
					.materiality-information-line__label {
						font-size: var(--px-16) !important;
					}
				}
				.LabelValue {
					display: flex;
					align-items: center;
				}
				.apply-color {
					color: var(--teal-600);
				}
			}
		}
	}
`;

export default TEMateriality;
